<div class="aggregates-page">
  <div class="header-row">
    <h2 class="title">Patient Aggregates &amp; Readiness</h2>
    <div class="inline">
      <input nz-input placeholder="Search by Patient UUID" [(ngModel)]="searchPatientId" style="width:300px" />
      <button nz-button nzType="primary" (click)="searchById()">
        <span nz-icon nzType="search"></span> Inspect
      </button>
    </div>
  </div>

  <!-- Readiness Funnel -->
  @if (stats(); as s) {
  <nz-divider nzText="Readiness Funnel"></nz-divider>
  <div class="funnel-row">
    <nz-card class="funnel-card">
      <nz-statistic nzTitle="Total Aggregates" [nzValue]="s.readiness_funnel.total_aggregates"></nz-statistic>
    </nz-card>
    <nz-card class="funnel-card">
      <nz-statistic nzTitle="Has Survey" [nzValue]="s.readiness_funnel.has_survey"
        [nzSuffix]="surveyPct"></nz-statistic>
      <ng-template #surveyPct>
        <span class="pct">({{ funnelPercent(s.readiness_funnel.has_survey, s.readiness_funnel.total_aggregates)
          }}%)</span>
      </ng-template>
      <nz-progress [nzPercent]="funnelPercent(s.readiness_funnel.has_survey, s.readiness_funnel.total_aggregates)"
        nzSize="small" nzStrokeColor="#1890ff" [nzShowInfo]="false"></nz-progress>
    </nz-card>
    <nz-card class="funnel-card">
      <nz-statistic nzTitle="Has Doctor Notes" [nzValue]="s.readiness_funnel.has_doctor_notes"
        [nzSuffix]="notesPct"></nz-statistic>
      <ng-template #notesPct>
        <span class="pct">({{ funnelPercent(s.readiness_funnel.has_doctor_notes, s.readiness_funnel.total_aggregates)
          }}%)</span>
      </ng-template>
      <nz-progress [nzPercent]="funnelPercent(s.readiness_funnel.has_doctor_notes, s.readiness_funnel.total_aggregates)"
        nzSize="small" nzStrokeColor="#faad14" [nzShowInfo]="false"></nz-progress>
    </nz-card>
    <nz-card class="funnel-card">
      <nz-statistic nzTitle="Has Microbiome" [nzValue]="s.readiness_funnel.has_microbiome"
        [nzSuffix]="microPct"></nz-statistic>
      <ng-template #microPct>
        <span class="pct">({{ funnelPercent(s.readiness_funnel.has_microbiome, s.readiness_funnel.total_aggregates)
          }}%)</span>
      </ng-template>
      <nz-progress [nzPercent]="funnelPercent(s.readiness_funnel.has_microbiome, s.readiness_funnel.total_aggregates)"
        nzSize="small" nzStrokeColor="#722ed1" [nzShowInfo]="false"></nz-progress>
    </nz-card>
    <nz-card class="funnel-card">
      <nz-statistic nzTitle="Fully Ready" [nzValue]="s.readiness_funnel.fully_ready"
        [nzSuffix]="readyPct"></nz-statistic>
      <ng-template #readyPct>
        <span class="pct">({{ funnelPercent(s.readiness_funnel.fully_ready, s.readiness_funnel.total_aggregates)
          }}%)</span>
      </ng-template>
      <nz-progress [nzPercent]="funnelPercent(s.readiness_funnel.fully_ready, s.readiness_funnel.total_aggregates)"
        nzSize="small" nzStrokeColor="#52c41a" [nzShowInfo]="false"></nz-progress>
    </nz-card>
    <nz-card class="funnel-card">
      <nz-statistic nzTitle="Has Routine" [nzValue]="s.readiness_funnel.has_routine"
        [nzSuffix]="routinePct"></nz-statistic>
      <ng-template #routinePct>
        <span class="pct">({{ funnelPercent(s.readiness_funnel.has_routine, s.readiness_funnel.total_aggregates)
          }}%)</span>
      </ng-template>
      <nz-progress [nzPercent]="funnelPercent(s.readiness_funnel.has_routine, s.readiness_funnel.total_aggregates)"
        nzSize="small" nzStrokeColor="#13c2c2" [nzShowInfo]="false"></nz-progress>
    </nz-card>
  </div>

  <!-- Status Distribution -->
  <nz-divider nzText="Pipeline Status Distribution"></nz-divider>
  <div class="status-distribution">
    <nz-card nzTitle="Routine Statuses" class="dist-card">
      @for (entry of s.routine_statuses | keyvalue; track entry.key) {
      <div class="dist-row">
        <nz-tag [nzColor]="statusColor(entry.key)">{{ entry.key }}</nz-tag>
        <span class="dist-count">{{ entry.value }}</span>
      </div>
      }
    </nz-card>
  </div>
  }

  <nz-divider nzText="Readiness Matrix"></nz-divider>

  <!-- Aggregates table (readiness matrix) -->
  <nz-table #aggTable [nzData]="aggregates()" [nzLoading]="loading()" [nzTotal]="total()" [nzPageIndex]="pageIndex()"
    [nzPageSize]="pageSize()" [nzFrontPagination]="false" [nzShowSizeChanger]="true" [nzPageSizeOptions]="[20, 50, 100]"
    (nzQueryParams)="onQueryParamsChange($event)" nzSize="small">
    <thead>
      <tr>
        <th>Patient</th>
        <th nzWidth="70px" nz-tooltip="Lifestyle survey">Survey</th>
        <th nzWidth="70px" nz-tooltip="Doctor notes">Notes</th>
        <th nzWidth="90px" nz-tooltip="Microbiome profile">Microbiome</th>
        <th nzWidth="70px">Ready</th>
        <th nzWidth="100px">Gen. Status</th>
        <th nzWidth="60px">Ver.</th>
        <th nzWidth="150px">Updated</th>
        <th nzWidth="160px">Actions</th>
      </tr>
    </thead>
    <tbody>
      @for (row of aggTable.data; track row.patient_id) {
      <tr>
        <td>{{ row.patient_name ?? row.patient_id }}</td>
        <td class="chip-cell">
          <span [ngClass]="row.has_survey ? 'chip-ok' : 'chip-missing'">{{ row.has_survey ? 'Y' : 'N' }}</span>
        </td>
        <td class="chip-cell">
          <span [ngClass]="row.has_doctor_notes ? 'chip-ok' : 'chip-missing'">{{ row.has_doctor_notes ? 'Y' : 'N'
            }}</span>
        </td>
        <td class="chip-cell">
          <span [ngClass]="row.has_microbiome ? 'chip-ok' : 'chip-missing'">{{ row.has_microbiome ? 'Y' : 'N' }}</span>
        </td>
        <td class="chip-cell">
          <span [ngClass]="row.is_ready ? 'chip-ok' : 'chip-missing'">{{ row.is_ready ? 'Y' : 'N' }}</span>
        </td>
        <td>
          <nz-tag [nzColor]="statusColor(row.routine_generation_status)">{{ row.routine_generation_status }}</nz-tag>
        </td>
        <td>{{ row.version }}</td>
        <td>{{ row.updated_at | date:'short' }}</td>
        <td>
          <button nz-button nzSize="small" nzType="link" (click)="viewDetail(row)">Detail</button>
          <button nz-button nzSize="small" nzType="link" (click)="refreshAggregate(row.patient_id)">Refresh</button>
        </td>
      </tr>
      } @empty {
      <tr>
        <td colspan="9" class="empty-cell">No aggregates found.</td>
      </tr>
      }
    </tbody>
  </nz-table>
</div>
